/**
 * Premium scroll & entrance animations for Merchant Antiques custom sections
 * (hero, services, testimonials, FAQ, CTA). Vanilla JS, no dependencies.
 * Respects prefers-reduced-motion throughout.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
   * 1. Staggered reveal-on-scroll for elements with [data-pa-reveal]
   * ------------------------------------------------------------------- */
  function initReveal(root) {
    root = root || document;
    var groups = root.querySelectorAll('[data-pa-group]');

    groups.forEach(function (group) {
      var items = Array.prototype.slice.call(group.querySelectorAll('[data-pa-reveal]'));
      if (!items.length) return;

      if (REDUCED_MOTION) {
        items.forEach(function (el) {
          el.classList.add('pa-in');
        });
        return;
      }

      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var el = entry.target;
            var index = items.indexOf(el);
            el.style.setProperty('--pa-delay', (index >= 0 ? index : 0) * 50 + 'ms');
            el.classList.add('pa-in');
            obs.unobserve(el);
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
      );

      items.forEach(function (el) {
        observer.observe(el);
      });
    });
  }

  /* ---------------------------------------------------------------------
   * 2. Hero entrance (runs once on load, not scroll-based)
   * ------------------------------------------------------------------- */
  function initHero(root) {
    root = root || document;
    var heroes = root.querySelectorAll('[data-pa-hero]');
    heroes.forEach(function (hero) {
      if (REDUCED_MOTION) {
        hero.classList.add('pa-hero-in');
        return;
      }
      requestAnimationFrame(function () {
        setTimeout(function () {
          hero.classList.add('pa-hero-in');
        }, 80);
      });
    });
  }

  /* ---------------------------------------------------------------------
   * 3. Subtle parallax on hero media
   * ------------------------------------------------------------------- */
  function initParallax(root) {
    if (REDUCED_MOTION) return;
    root = root || document;
    var layers = Array.prototype.slice.call(root.querySelectorAll('[data-pa-parallax]'));
    if (!layers.length) return;

    var ticking = false;
    function update() {
      layers.forEach(function (layer) {
        var rect = layer.parentElement.getBoundingClientRect();
        var speed = parseFloat(layer.getAttribute('data-pa-parallax')) || 0.15;
        var offset = rect.top * speed;
        layer.style.transform = 'translate3d(0,' + offset + 'px,0) scale(1.08)';
      });
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------------------------------------------------------------------
   * 4. Count-up numbers [data-pa-counter="1200"]
   * ------------------------------------------------------------------- */
  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-pa-counter')) || 0;
    var suffix = el.getAttribute('data-pa-suffix') || '';
    var duration = 1400;
    var start = null;

    if (REDUCED_MOTION) {
      el.textContent = target + suffix;
      return;
    }

    function step(timestamp) {
      if (!start) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      var value = Math.round(target * easeOutExpo(progress));
      el.textContent = value + suffix;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters(root) {
    root = root || document;
    var counters = root.querySelectorAll('[data-pa-counter]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          animateCount(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
   * 5. FAQ accordion
   * ------------------------------------------------------------------- */
  function initAccordions(root) {
    root = root || document;
    var faqs = root.querySelectorAll('[data-pa-faq-item]');
    faqs.forEach(function (item) {
      var trigger = item.querySelector('[data-pa-faq-trigger]');
      if (!trigger || trigger.__paBound) return;
      trigger.__paBound = true;
      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        var parent = item.closest('[data-pa-faq-list]');
        if (parent) {
          parent.querySelectorAll('[data-pa-faq-item].is-open').forEach(function (openItem) {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
              var t = openItem.querySelector('[data-pa-faq-trigger]');
              if (t) t.setAttribute('aria-expanded', 'false');
            }
          });
        }
        item.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ---------------------------------------------------------------------
   * 6. Testimonial carousel (scroll-snap track + dot nav + autoplay)
   * ------------------------------------------------------------------- */
  function initCarousels(root) {
    root = root || document;
    var carousels = root.querySelectorAll('[data-pa-carousel]');

    carousels.forEach(function (carousel) {
      if (carousel.__paBound) return;
      carousel.__paBound = true;

      var track = carousel.querySelector('[data-pa-carousel-track]');
      var dotsWrap = carousel.querySelector('[data-pa-carousel-dots]');
      var prevBtn = carousel.querySelector('[data-pa-carousel-prev]');
      var nextBtn = carousel.querySelector('[data-pa-carousel-next]');
      if (!track) return;

      var slides = Array.prototype.slice.call(track.children);
      var dots = [];

      if (dotsWrap) {
        slides.forEach(function (_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'pa-carousel-dot';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.addEventListener('click', function () {
            goTo(i);
          });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
      }

      function setActive(index) {
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });
      }

      function goTo(index) {
        var slide = slides[index];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
      }

      function currentIndex() {
        var scrollLeft = track.scrollLeft;
        var closest = 0;
        var closestDist = Infinity;
        slides.forEach(function (slide, i) {
          var dist = Math.abs(slide.offsetLeft - track.offsetLeft - scrollLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        return closest;
      }

      var scrollTicking = false;
      track.addEventListener(
        'scroll',
        function () {
          if (scrollTicking) return;
          scrollTicking = true;
          requestAnimationFrame(function () {
            setActive(currentIndex());
            scrollTicking = false;
          });
        },
        { passive: true }
      );

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(Math.max(0, currentIndex() - 1));
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(Math.min(slides.length - 1, currentIndex() + 1));
        });
      }

      setActive(0);

      if (!REDUCED_MOTION && carousel.hasAttribute('data-pa-autoplay')) {
        var delay = parseInt(carousel.getAttribute('data-pa-autoplay'), 10) || 5000;
        var timer = null;

        function start() {
          timer = setInterval(function () {
            var next = (currentIndex() + 1) % slides.length;
            goTo(next);
          }, delay);
        }
        function stop() {
          if (timer) clearInterval(timer);
        }
        start();
        carousel.addEventListener('mouseenter', stop);
        carousel.addEventListener('mouseleave', start);
        carousel.addEventListener('focusin', stop);
        carousel.addEventListener('focusout', start);
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Init
   * ------------------------------------------------------------------- */
  function initAll(root) {
    initReveal(root);
    initHero(root);
    initParallax(root);
    initCounters(root);
    initAccordions(root);
    initCarousels(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });
  }
})();
